import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOnboardingStore } from '../store/onboardingStore'
import { Button } from '../components/Button'
import { HeaderWash, V2, V2Icon } from '../components/v2'
import { usesAppLook } from '../lib/variant'
import { useScreen1ExitTracking } from '../lib/screen1Exit'

// Screen 1 copy, order, timing and CTA are IDENTICAL in both organic arms so a
// later screen-1 test stays clean — organic-v2 only swaps the emoji for the
// app's illustrated marks (`icon`) and the 4.33 type/colour.
const painPoints = [
  { emoji: '🏷️', icon: 'pain-labels', text: 'Struggling to read food labels for kidney safety' },
  { emoji: '🔍', icon: 'pain-hidden', text: 'Hidden phosphorus and sodium in everyday foods' },
  { emoji: '😰', icon: 'pain-conflicting', text: 'Conflicting advice about what you can eat' },
  { emoji: '😟', icon: 'pain-fear', text: 'Fear of eating the wrong thing and making it worse' },
  { emoji: '📊', icon: 'pain-tracking', text: 'No easy way to track kidney-specific nutrients' },
]

export function PainPointsStep() {
  const { goNext } = useOnboardingStore()
  const [visibleCount, setVisibleCount] = useState(0)
  const [showButton, setShowButton] = useState(false)
  const v2 = usesAppLook()
  // Both arms: one funnel_screen1_exit (continue | leave) per visit.
  const reportContinue = useScreen1ExitTracking()

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = []
    painPoints.forEach((_, i) => {
      timers.push(setTimeout(() => setVisibleCount(i + 1), 600 * (i + 1)))
    })
    timers.push(setTimeout(() => setShowButton(true), 600 * (painPoints.length + 1)))
    return () => timers.forEach(clearTimeout)
  }, [])

  return (
    <div className={v2 ? `min-h-dvh flex flex-col relative ${V2.page}` : 'min-h-dvh flex flex-col bg-white'}>
      {v2 && <HeaderWash />}
      <div className={v2 ? 'flex-1 flex flex-col items-center justify-center px-6 py-12 relative z-10' : 'flex-1 flex flex-col items-center justify-center px-6 py-12'}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <h1 className={v2 ? `${V2.h1} text-center mb-3` : 'text-3xl font-bold text-gray-900 text-center mb-3'}>
            Managing kidney health<br />is overwhelming
          </h1>
          <p className={v2 ? `${V2.textSecondary} text-center mb-10` : 'text-gray-500 text-center mb-10'}>
            If any of these sound familiar, you're not alone
          </p>

          <div className="space-y-4 mb-10">
            {painPoints.map((point, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={i < visibleCount ? { opacity: 1, x: 0 } : {}}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                className={v2
                  ? 'flex items-center gap-4 bg-white border border-red-100 rounded-xl px-4 py-3'
                  : 'flex items-center gap-4 bg-red-50 rounded-xl px-5 py-4'}
              >
                {v2 ? <V2Icon name={point.icon} size={36} /> : <span className="text-2xl">{point.emoji}</span>}
                <span className={v2 ? `${V2.text} font-semibold text-sm` : 'text-gray-700 font-medium text-sm'}>{point.text}</span>
              </motion.div>
            ))}
          </div>

          {showButton && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Button
                onClick={() => {
                  reportContinue()
                  goNext()
                }}
              >
                That's me
              </Button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  )
}
